/**
 * The load topic — the contract between writer and consumers.
 *
 * WHAT THIS CAN HONESTLY SHOW. The topic itself is Pub/Sub, and nothing in this
 * service reads the Pub/Sub monitoring API — so subscription backlog, oldest
 * unacked age and dead-letter depth are NOT available here, and this page says
 * so rather than rendering a zero that would read as "healthy".
 *
 * What IS evidence about the topic is end-to-end: the writer published, and
 * rows landed in the consumer's table. Consumer lag is therefore the real
 * signal on this page — if it stays small, delivery is working, whatever the
 * subscription counters would have said.
 */

import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Card from '@mui/joy/Card'
import Chip from '@mui/joy/Chip'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import * as React from 'react'
import { loadPipelineApi } from 'api/loadPipeline'
import { ErrorState, Loading, Stat, StatRow, formatLag, useLoadPipelineData } from './components/shared'

/**
 * What the topic carries (ADR-029): ONE message, the load update, in two kinds. Static: it is the contract. Each row is a
 * case from the spec, with what Load Search and Market Intel do with it.
 */
const UPDATES: Array<{ update: string; kind: 'SEEN' | 'UPDATED'; stage: string; search: string; intel: boolean }> = [
    { update: 'a board shows a posting', kind: 'SEEN', stage: 'POSTED', search: 'the listing, and its facts as the board states them', intel: true },
    {
        update: 'a board pulls a posting without saying why',
        kind: 'UPDATED',
        stage: 'POSTED',
        search: 'closes the listing and its shipment (covered, inferred)',
        intel: false,
    },
    { update: 'a board states the posting expired', kind: 'UPDATED', stage: 'POSTED', search: 'hides that listing only', intel: false },
    { update: 'a posting’s opened details', kind: 'UPDATED', stage: 'POSTED', search: 'commodity and stop notes on that listing', intel: false },
    {
        update: 'a broker’s reply',
        kind: 'UPDATED',
        stage: 'EXPOSED',
        search: 'facts on every public listing of the load; the quote is the search rate',
        intel: false,
    },
    { update: 'a rate con', kind: 'SEEN', stage: 'BOOKED', search: 'facts on every public listing; the booking closes nothing yet', intel: false },
    { update: 'a route distance from an enricher', kind: 'UPDATED', stage: '—', search: 'ignored', intel: false },
]

export default function LoadPipelineTopicPage() {
    // Lag alone — an index probe, not the overview's full scans, which this page never shows.
    const { data, error, loading } = useLoadPipelineData(() => loadPipelineApi.getFreshness(), [])
    const lag = data ? formatLag(data.lagSeconds) : null

    return (
        <Box sx={{ p: 3 }}>
            <Typography level="body-sm" sx={{ mb: 2, color: 'text.tertiary' }}>
                The contract every writer publishes and every consumer reads (ADR-027, ADR-029). Volume and freshness are already on the Overview tab — this
                page adds only what is specific to the topic itself.
            </Typography>

            {loading ? <Loading /> : null}
            {error ? <ErrorState error={error} /> : null}

            {data && !loading ? (
                <>
                    <StatRow>
                        <Stat
                            label="Consumer lag"
                            value={lag?.text ?? '—'}
                            tone={lag?.tone}
                            hint="Newest stored bucket vs. now — the end-to-end evidence that delivery is working, in place of subscription metrics this page cannot read"
                        />
                    </StatRow>

                    <Alert color="neutral" variant="soft" sx={{ my: 2 }}>
                        <Box>
                            <Typography level="body-sm">
                                <strong>Subscription backlog and dead-letter depth are not shown.</strong> They live in the Pub/Sub monitoring API, which this
                                service does not read.
                            </Typography>
                            <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.tertiary' }}>
                                Rendering them as zero would be worse than omitting them — a stalled subscription and an idle one look identical at zero.
                                Consumer lag above is the honest proxy: it can only stay small if messages are arriving.
                            </Typography>
                        </Box>
                    </Alert>

                    <Card>
                        <Typography level="title-md">What the topic carries</Typography>
                        <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 1 }}>
                            One message — the load update — in two kinds. <code>SEEN</code> is the source’s whole record of the load, <code>UPDATED</code> only
                            what changed or was learned; the stage comes from the source, never the wire (ADR-029). Every consumer parses it strictly and
                            records what it refuses. Market Intel reads a posting’s posted price; Load Search reads the rest by stage.
                        </Typography>
                        <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                            <Table size="sm">
                                <thead>
                                    <tr>
                                        <th style={{ width: 260 }}>Update</th>
                                        <th style={{ width: 110 }}>Kind</th>
                                        <th style={{ width: 100 }}>Stage</th>
                                        <th>Load Search</th>
                                        <th style={{ width: 150 }}>Market Intel</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {UPDATES.map((u) => (
                                        <tr key={u.update}>
                                            <td>
                                                <Typography level="body-sm">{u.update}</Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                                                    {u.kind}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                                                    {u.stage}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-sm">{u.search}</Typography>
                                            </td>
                                            <td>
                                                {u.intel ? (
                                                    <Chip size="sm" color="success" variant="soft">
                                                        consumed
                                                    </Chip>
                                                ) : (
                                                    <Chip size="sm" variant="soft">
                                                        ignored
                                                    </Chip>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Sheet>
                    </Card>
                </>
            ) : null}
        </Box>
    )
}
